'use client';
import { Anchor } from '@repo/ui/src/components/Anchor';
import Markdown from '@repo/ui/src/components/Markdown';
import { classNames } from '@repo/ui/src/utils/classNames';
import Image from '@repo/ui/src/components/Image/New';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { navigationClicked } from '@repo/ui/src/services/analytics';
import { ProductCardProps } from './_ProductCard';
import React from 'react';
import { appConfig } from '../../utils/appConfig';

const useBadgeText = (badge?: string) => {
  const { t } = useTranslation();
  if (badge === 'new') {
    return t('productDetailPageContent.newText', undefined, { default: 'New' });
  }
  if (badge === 'sale') {
    return t('productDetailPageContent.saleText', undefined, { default: 'Sale' });
  }
};

const ProductCardMeater = ({
  title,
  price,
  mainLink,
  links,
  badge,
  image,
  primaryHeader,
  secondaryHeader,
}: Omit<ProductCardProps, 'hoverImage'>) => {
  const badgeText = useBadgeText(badge);
  if (!mainLink && !title) return null;
  const hasImage = image?.image?.url || image?.image?.id;
  const mobileImage = image;

  return (
    <Anchor
      {...mainLink}
      className={classNames(
        'group inline-block w-full h-full',
        appConfig.isTraeger ? 'bg-neutral-100' : 'bg-white',
      )}
      onClick={() =>
        navigationClicked({
          nav_type: 'Top Navigation',
          clicked_text: title ?? '',
          primary_header: primaryHeader ?? '',
          secondary_header: secondaryHeader ?? '',
          tertiary_header: title ?? '',
        })
      }
    >
      <div
        className={classNames(
          'relative text-neutral-700 grid gap-x-4 sm:block',
          'outline outline-1 outline-neutral-300 p-2 sm:p-4 h-full',
          hasImage &&
            'sm:grid-cols-[minmax(0,4fr)_minmax(0,5fr)] grid-cols-[minmax(0,2fr)_minmax(0,7fr)] items-center',
        )}
        role="group"
        aria-label="product"
      >
        {hasImage && (
          <div className="overflow-visible justify-self-start sm:hidden w-fit h-20">
            <Image
              className="w-20 h-20 object-cover"
              src={buildImageSrc(mobileImage, 96)}
              width={96}
              height={96}
              alt={mobileImage?.altTag ?? ''}
              data-testid="mobile-image"
            />
          </div>
        )}
        <div>
          <div
            className={classNames(
              'flex flex-row justify-end',
              'sm:static absolute right-4 top-4',
              !badgeText && 'invisible',
            )}
          >
            <div className="px-2 h-fit bg-brand-300 min-h-5 py-[3px]">
              <p className="!text-white tra-text-web-utl-label">{badgeText}</p>
            </div>
          </div>
          {hasImage && (
            <div className="overflow-visible hidden sm:block">
              <div className="relative h-[160px] w-full">
                <Image
                  className={classNames(
                    'h-[155px] w-full object-contain',
                    'transform transition duration-300',
                    'ease-[cubic-bezier(0.33,1,0.68,1)] group-hover:scale-110',
                  )}
                  src={buildImageSrc(image, 300)}
                  width={300}
                  height={300}
                  alt={image?.altTag ?? ''}
                  data-testid="desktop-image"
                />
              </div>
            </div>
          )}
          <h6
            className={classNames(
              'mt-2 font-bold',
              appConfig.isTraeger ? 'tra-text-web-sub-5' : 'tra-text-web-heading-4',
            )}
          >
            {title}
          </h6>
          {price && <Markdown className="!mb-0 py-2 font-bold">{price}</Markdown>}
          <div className="flex flex-row w-full justify-start gap-x-6">
            {links?.map((link, i) => (
              <Anchor
                {...link}
                key={i}
                className="tra-text-web-body-m-link"
                onClick={(e) => {
                  e.stopPropagation();
                  navigationClicked({
                    nav_type: 'Top Navigation',
                    clicked_text: link.displayName ?? '',
                    primary_header: primaryHeader ?? '',
                    secondary_header: secondaryHeader ?? '',
                    tertiary_header: `${title ? title + ' ' : ''}${link.displayName}`,
                  });
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </Anchor>
  );
};

export default ProductCardMeater;
